import React, { useState } from 'react';
import { login, register } from '../services/api';

export const LandingPage = ({ onLoginSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    institution: '',
    profession: '',
    country: '',
    city: '',
  });

  const resetForm = () => {
    setError('');
    setFormData({
      email: '', password: '', confirmPassword: '',
      firstName: '', lastName: '',
      institution: '', profession: '', country: '', city: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await login(formData.email, formData.password);
      if (response && response.user && onLoginSuccess) {
        onLoginSuccess(response.user);
      }
    } catch (err) {
      if (!err.response || err.code === 'ERR_NETWORK') {
        setError('Authentication server not available. Please check your connection and try again.');
      } else if (err.response?.status === 401) {
        setError('Invalid email or password');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        institution: formData.institution,
        profession: formData.profession,
        country: formData.country,
        city: formData.city,
      });
      if (response && response.user && onLoginSuccess) {
        onLoginSuccess(response.user);
      }
    } catch (err) {
      if (!err.response || err.code === 'ERR_NETWORK') {
        setError('Server not available. Please try again.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const openModal = (loginMode = true) => {
    setIsLoginMode(loginMode);
    resetForm();
    setShowModal(true);
  };

  return (
    <div 
      className="landing-page"
      style={{ 
        minHeight: '100vh',
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        background: '#ffffff',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}
    >
      {/* Navbar */}
      <nav style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 0',
        background: 'white',
        maxWidth: '1500px',
        margin: '0 auto',
        width: '100%',
        paddingLeft: '60px',
        paddingRight: '60px',
        boxSizing: 'border-box'
      }}>
        {/* Logo */}
        <img 
          src="/Aiatus_logo.png" 
          alt="Aiatus AI" 
          style={{ height: '65px', objectFit: 'contain' }}
        />

        {/* Nav Buttons */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button
            onClick={() => openModal(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#374151',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              padding: '10px 20px'
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => openModal(false)}
            style={{
              background: '#2563eb',
              border: 'none',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              padding: '12px 24px',
              borderRadius: '25px',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)'
            }}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="hero-section" style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #e0e7ff 100%)',
        padding: '80px 0 100px'
      }}>
        <div className="hero-content" style={{
          maxWidth: '1500px',
          margin: '0 auto',
          padding: '0 60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '60px',
          boxSizing: 'border-box'
        }}>
        {/* Left Content */}
        <div className="hero-left" style={{ maxWidth: '480px' }}>
          <div className="hero-badge" style={{
            display: 'inline-block',
            background: '#dbeafe',
            color: '#1d4ed8',
            fontSize: '12px',
            fontWeight: '600',
            padding: '8px 16px',
            borderRadius: '20px',
            marginBottom: '24px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            AI-POWERED GUIDELINE ASSISTANT
          </div>
          
          <h1 style={{
            fontSize: '52px',
            fontWeight: '800',
            color: '#0f172a',
            lineHeight: '1.1',
            margin: '0 0 24px 0'
          }}>
            Smart assistant<br />
            <span style={{ color: '#2563eb' }}>for Pediatric Transplantation.</span>
          </h1>
          
          <p style={{
            fontSize: '19px',
            color: '#64748b',
            lineHeight: '1.7',
            margin: '0 0 32px 0'
          }}>
            Get instant answers to your Pediatric Transplantation questions based on clinical guidelines. Your AI assistant for evidence-based recommendations.
          </p>
          
          <button
            onClick={() => openModal(false)}
            style={{
              background: '#0f172a',
              border: 'none',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              padding: '16px 32px',
              borderRadius: '30px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.3)'
            }}
          >
            Start Now
          </button>
        </div>

        {/* Right Content - App Preview */}
        <div className="hero-right" style={{
          position: 'relative',
          width: '750px'
        }}>
          {/* Browser Frame */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            {/* Browser Header */}
            <div style={{
              background: '#f1f5f9',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }}></div>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }}></div>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e' }}></div>
              <div style={{
                flex: 1,
                background: '#e2e8f0',
                height: '28px',
                borderRadius: '6px',
                marginLeft: '12px'
              }}></div>
            </div>
            
            {/* App Preview Image */}
            <img 
              src="/chatbot.png" 
              alt="Chatbot Preview" 
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          </div>
        </div>
        </div>
      </div>

      {/* Partners Section: Aiatus, Cerrahpaşa, CERTAIN */}
      <div style={{
        borderTop: '1px solid #e2e8f0'
      }}>
        <div className="partners" style={{
          maxWidth: '1500px',
          margin: '0 auto',
          padding: '60px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '80px',
          boxSizing: 'border-box'
        }}>
          <img src="/Aiatus_logo.png" alt="Aiatus" style={{ height: '72px', objectFit: 'contain' }} />
          <img src="/cerrahpasa_tip_logo.png" alt="Cerrahpaşa Tıp" style={{ height: '100px', objectFit: 'contain' }} />
          <img src="/certain_logo.png" alt="CERTAIN" style={{ height: '88px', objectFit: 'contain' }} />
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        background: '#0b1b3f',
        color: '#cbd5e1',
        padding: '56px 0 28px'
      }}>
        <div className="footer-content" style={{
          maxWidth: '1500px',
          margin: '0 auto',
          padding: '0 60px',
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 1fr',
          gap: '40px',
          boxSizing: 'border-box'
        }}>
          <div className="footer-brand">
            <img
              src="/Aiatus_logo.png"
              alt="AIATUS"
              style={{ height: '40px', objectFit: 'contain', marginBottom: '14px' }}
            />
            <p className="footer-description" style={{
              margin: 0,
              fontSize: '16px',
              lineHeight: '1.7',
              color: '#94a3b8',
              maxWidth: '320px'
            }}>
              Pediatric Transplantation guideline assistant for fast, evidence-based decisions.
            </p>
          </div>

          <div>
            <h4 className="footer-heading" style={{ margin: '0 0 14px 0', color: '#f8fafc', fontSize: '17px', letterSpacing: '0.3px' }}>PLATFORM</h4>
            <div className="footer-links" style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '16px', color: '#94a3b8' }}>
              <span>AI Guideline Search</span>
              <span>Clinical References</span>
              <span>Evidence Summaries</span>
            </div>
          </div>

          <div>
            <h4 className="footer-heading" style={{ margin: '0 0 14px 0', color: '#f8fafc', fontSize: '17px', letterSpacing: '0.3px' }}>CONTACT</h4>
            <div className="footer-links" style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '16px' }}>
              <a href="mailto:info@aiatus.com" style={{ color: '#94a3b8', textDecoration: 'none' }}>info@aiatus.com</a>
              <a href="https://aiatus.com" target="_blank" rel="noreferrer" style={{ color: '#94a3b8', textDecoration: 'none' }}>aiatus.com</a>
              <a href="#" style={{ color: '#94a3b8', textDecoration: 'none' }}>Request a Demo</a>
            </div>
          </div>
        </div>

        <div className="footer-bottom" style={{
          maxWidth: '1500px',
          margin: '24px auto 0',
          padding: '18px 60px 0',
          borderTop: '1px solid rgba(148, 163, 184, 0.2)',
          color: '#64748b',
          fontSize: '15px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px'
        }}>
          <span className="footer-copyright">© 2026 Pediatric Transplantation AI Assistant. All rights reserved.</span>
          <div className="footer-social" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <a
              href="https://aiatus.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit AIATUS website"
              style={{ color: '#64748b', display: 'inline-flex', transition: 'color 0.2s ease' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3 12H21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M12 3C9.5 5.6 8 8.9 8 12C8 15.1 9.5 18.4 12 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M12 3C14.5 5.6 16 8.9 16 12C16 15.1 14.5 18.4 12 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </a>
            <a
              href="mailto:info@aiatus.com"
              aria-label="Send email to info@aiatus.com"
              style={{ color: '#64748b', display: 'inline-flex', transition: 'color 0.2s ease' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M4 7L12 13L20 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </footer>

      {/* Login / Register Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          boxSizing: 'border-box',
        }} onClick={() => setShowModal(false)}>
          <div className="login-modal" style={{
            background: 'white',
            borderRadius: '20px',
            width: isLoginMode ? '440px' : '500px',
            maxWidth: '100%',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
          }} onClick={(e) => e.stopPropagation()}>

            {/* Scrollable content */}
            <div style={{ overflowY: 'auto', padding: '32px 36px 24px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', textAlign: 'center', margin: '0 0 6px 0' }}>
                {isLoginMode ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', margin: '0 0 20px 0' }}>
                {isLoginMode ? 'Sign in to access the AI Assistant' : 'Register to get started'}
              </p>

              {/* Tab Switcher */}
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '10px', padding: '3px', marginBottom: '20px' }}>
                <button onClick={() => { setIsLoginMode(true); resetForm(); }}
                  style={{ flex: 1, padding: '9px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                    background: isLoginMode ? '#2563eb' : 'transparent', color: isLoginMode ? 'white' : '#64748b', transition: 'all 0.2s' }}>
                  Sign In
                </button>
                <button onClick={() => { setIsLoginMode(false); resetForm(); }}
                  style={{ flex: 1, padding: '9px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                    background: !isLoginMode ? '#2563eb' : 'transparent', color: !isLoginMode ? 'white' : '#64748b', transition: 'all 0.2s' }}>
                  Register
                </button>
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', color: '#dc2626', fontSize: '13px', textAlign: 'center' }}>
                  {error}
                </div>
              )}

              <form onSubmit={isLoginMode ? handleLogin : handleRegister}>
                {/* Register: First Name & Last Name */}
                {!isLoginMode && (
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>First Name *</label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="First name"
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                        onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Last Name *</label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Last name"
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                        onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                    </div>
                  </div>
                )}

                {/* Email */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Email {!isLoginMode && '*'}
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter your email"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>

                {/* Password */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Password {!isLoginMode && '*'}
                  </label>
                  <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="••••••••"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>

                {/* Confirm Password (Register) */}
                {!isLoginMode && (
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Confirm Password *</label>
                    <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} placeholder="••••••••"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                )}

                {/* Additional fields (Register) */}
                {!isLoginMode && (
                  <>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Institution</label>
                        <input type="text" name="institution" value={formData.institution} onChange={handleInputChange} placeholder="e.g. Istanbul University"
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                          onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Profession</label>
                        <input type="text" name="profession" value={formData.profession} onChange={handleInputChange} placeholder="e.g. Nephrologist"
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                          onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Country</label>
                        <input type="text" name="country" value={formData.country} onChange={handleInputChange} placeholder="Country"
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                          onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>City</label>
                        <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="City"
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                          onFocus={(e) => e.target.style.borderColor = '#2563eb'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                      </div>
                    </div>
                  </>
                )}

                <button type="submit" disabled={loading}
                  style={{ width: '100%', padding: '12px', background: loading ? '#93c5fd' : '#2563eb', border: 'none', borderRadius: '10px',
                    color: 'white', fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}>
                  {loading ? (isLoginMode ? 'Signing in...' : 'Creating account...') : (isLoginMode ? 'Sign In' : 'Create Account')}
                </button>
              </form>

              <p style={{ textAlign: 'center', fontSize: '13px', color: '#64748b', marginTop: '16px', marginBottom: '0' }}>
                {isLoginMode ? "Don't have an account? " : "Already have an account? "}
                <button onClick={() => { setIsLoginMode(!isLoginMode); resetForm(); }}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: '600', fontSize: '13px', padding: 0 }}>
                  {isLoginMode ? 'Register' : 'Sign In'}
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      
      {/* Responsive Styles */}
      <style>{`
        /* Landing page: scrollable when content overflows */
        .landing-page {
          -webkit-overflow-scrolling: touch;
        }

        @media (max-width: 768px) {
          /* Mobile navbar */
          nav {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }

          nav img {
            height: 50px !important;
          }

          nav button {
            font-size: 14px !important;
            padding: 8px 16px !important;
          }

          /* Hero section mobile */
          .hero-section {
            padding: 40px 20px !important;
          }

          .hero-content {
            flex-direction: column !important;
            gap: 40px !important;
            padding: 0 20px !important;
          }

          .hero-left {
            max-width: 100% !important;
            text-align: center;
          }

          .hero-left h1 {
            font-size: 36px !important;
            text-align: center;
          }

          .hero-left p {
            font-size: 16px !important;
            text-align: center;
          }

          .hero-left button {
            margin: 0 auto;
          }

          .hero-right {
            width: 100% !important;
            max-width: 500px !important;
          }

          /* Partners section mobile */
          .partners {
            flex-direction: column !important;
            gap: 40px !important;
            padding: 40px 20px !important;
          }

          .partners img {
            height: 50px !important;
          }

          .footer-content {
            grid-template-columns: 1fr 1fr !important;
            gap: 28px !important;
            padding: 0 20px !important;
          }

          .footer-brand {
            grid-column: 1 / -1;
            max-width: 100%;
          }

          .footer-heading {
            font-size: 16px !important;
          }

          .footer-links,
          .footer-description {
            font-size: 15px !important;
          }

          .footer-bottom {
            flex-direction: column !important;
            align-items: flex-start !important;
            padding: 18px 20px 0 !important;
            font-size: 14px !important;
            gap: 14px !important;
          }

          .footer-social {
            gap: 14px !important;
          }

          /* Login modal mobile */
          .login-modal {
            width: calc(100% - 40px) !important;
            padding: 30px 24px !important;
            margin: 20px !important;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          /* Tablet adjustments */
          nav {
            padding-left: 40px !important;
            padding-right: 40px !important;
          }

          .hero-content {
            padding: 0 40px !important;
            gap: 40px !important;
          }

          .hero-left h1 {
            font-size: 44px !important;
          }

          .hero-right {
            width: 600px !important;
          }

          .partners {
            padding: 50px 40px !important;
          }

          .footer-content {
            grid-template-columns: 1fr 1fr !important;
            padding: 0 40px !important;
          }

          .footer-bottom {
            padding: 18px 40px 0 !important;
            font-size: 14px !important;
          }
        }

        @media (max-width: 480px) {
          /* Small mobile */
          .hero-left h1 {
            font-size: 32px !important;
          }

          .hero-badge {
            font-size: 10px !important;
            padding: 6px 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
